import {
  FiShoppingCart,
  FiFileText,
  FiCreditCard,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function SalesActivityTimeline({
  invoices = [],
  payments = [],
  orders = [],
}) {
  const navigate = useNavigate();

  const activities = useMemo(() => {
    const orderActivities = orders.map((order) => ({
      id: `order-${order.id}`,
      title: getOrderTitle(order.status),
      description: `${order.salesOrderNumber} for ${
        order.customer?.fullName || "customer"
      }`,
      date: order.orderDate,
      icon: FiShoppingCart,
      color: order.status === "DELIVERED" ? "green" : "blue",
      onClick: () =>
        navigate(`/admindashboard/sales/orders/${order.id}`),
    }));

    const invoiceActivities = invoices.map((invoice) => ({
      id: `invoice-${invoice.id}`,
      title:
        invoice.status === "ISSUED"
          ? "Invoice Issued"
          : invoice.status === "CANCELLED"
            ? "Invoice Cancelled"
            : "Invoice Draft Created",
      description: `${invoice.invoiceCode} for ${invoice.customerNameSnapshot}`,
      date: invoice.invoiceDate,
      icon: FiFileText,
      color:
        invoice.status === "CANCELLED"
          ? "red"
          : invoice.status === "ISSUED"
            ? "green"
            : "blue",
      onClick: () =>
        navigate(`/admindashboard/sales/invoice/${invoice.id}`),
    }));

    const paymentActivities = payments.map((payment) => ({
      id: `payment-${payment.id}`,
      title:
        payment.status === "COMPLETED"
          ? "Payment Received"
          : "Payment Receipt Voided",
      description: `${formatMoney(payment.amount)} received from ${
        payment.customer?.fullName || "customer"
      } via ${toTitle(payment.paymentMode)}`,
      date: payment.paymentDate,
      icon: FiCreditCard,
      color: payment.status === "COMPLETED" ? "purple" : "red",
      onClick: () => navigate("/admindashboard/sales/payment"),
    }));

    return [...orderActivities, ...invoiceActivities, ...paymentActivities]
      .filter((activity) => activity.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 7);
  }, [invoices, orders, payments, navigate]);

  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    red: "bg-red-50 text-red-600",
    yellow: "bg-yellow-50 text-yellow-600",
  };

  return (
    <div className="bg-white border border-gray-100 rounded-3xl">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-xl font-bold">Sales Activity Timeline</h2>

        <p className="text-sm text-gray-500 mt-1">
          Latest activity across your accessible stores
        </p>
      </div>

      <div className="p-6">
        {activities.length ? (
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gray-200" />

            <div className="space-y-8">
              {activities.map((activity) => {
                const Icon = activity.icon;

                return (
                  <button
                    key={activity.id}
                    onClick={activity.onClick}
                    className="relative flex gap-5 w-full text-left group"
                  >
                    <div
                      className={`relative z-10 h-12 w-12 shrink-0 rounded-2xl flex items-center justify-center shadow-sm ${
                        colors[activity.color]
                      }`}
                    >
                      <Icon size={20} />
                    </div>

                    <div className="flex-1 pb-2 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-primary-700 transition">
                            {activity.title}
                          </h3>

                          <p className="text-gray-500 mt-1 truncate">
                            {activity.description}
                          </p>
                        </div>

                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatRelativeTime(activity.date)}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="py-14 text-center text-gray-500">
            <FiClock size={28} className="mx-auto text-gray-300 mb-3" />
            No recent sales activity found.
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-3xl flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing latest {activities.length} activities
        </p>

        <button
          onClick={() => navigate("/admindashboard/sales/orders/list")}
          className="text-primary-700 font-semibold hover:underline"
        >
          View Sales Orders
        </button>
      </div>
    </div>
  );
}

function getOrderTitle(status) {
  const titles = {
    DRAFT: "Sales Order Draft Created",
    CONFIRMED: "Sales Order Confirmed",
    PARTIALLY_DELIVERED: "Sales Order Partially Delivered",
    DELIVERED: "Sales Order Delivered",
    CANCELLED: "Sales Order Cancelled",
  };

  return titles[status] || "Sales Order Updated";
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatRelativeTime(value) {
  if (!value) return "—";

  const difference = Date.now() - new Date(value).getTime();

  if (difference < 60 * 1000) return "Just now";

  const minutes = Math.floor(difference / (60 * 1000));
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}